import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { makeRequest } from "../../utils/request";
import { PageLayout } from "../../components/PageLayout";
import { Alert, AlertDescription, Badge, Button, Dialog, DropdownField, Icon, InputField, Spinner, SwitchField, Text, View } from "../../components/base";
import { IconButton } from "../../components/base/IconButton";
import {
  ActivityBarChart,
  ADMIN_CHART_COLORS,
  AdminDetailDrawer,
  AdminTable,
  Field,
  RangePicker,
  rangeToDates,
  Section,
  useAdminFetch,
} from "../../components/admin";
import type { AdminColumn, RangeKey } from "../../components/admin";
import { getColorForName } from "../../utils/colors";
import { formatCount, formatDateShort, formatDollars, formatRelative } from "../../utils/adminFormat";
import type { OrgActivityDetail, OrgActivityRow } from "../../types/admin";

const TIER_OPTIONS = [
  { label: "Community", value: "community" },
  { label: "Enterprise", value: "enterprise" },
];

type OrganizationSettings = {
  id: string;
  name: string;
  tier: string;
  outboundEnabled: boolean;
  sessionCostPerMinute?: number;
  evalsTokenCostMultiplier?: number;
  completionsTokenCostMultiplier?: number;
  phoneNumberMonthlyRate?: number;
};

type EditFormState = {
  tier: string;
  outboundEnabled: boolean;
  sessionCostPerMinute: string;
  evalsTokenCostMultiplier: string;
  completionsTokenCostMultiplier: string;
  phoneNumberMonthlyRate: string;
};

const EMPTY_EDIT_FORM: EditFormState = {
  tier: "community",
  outboundEnabled: false,
  sessionCostPerMinute: "",
  evalsTokenCostMultiplier: "",
  completionsTokenCostMultiplier: "",
  phoneNumberMonthlyRate: "",
};

const inviteStatusVariant = (status: string) => {
  if (status === "accepted") return "success";
  if (status === "pending") return "warning";
  return "default";
};

const OrgAvatar = ({ name }: { name: string }) => (
  <View className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-medium text-white ${getColorForName(name || "?")}`}>
    {(name || "?").charAt(0).toUpperCase()}
  </View>
);

const CreateOrganizationDialog = ({ isOpen, onClose, onCreated }: { isOpen: boolean; onClose: () => void; onCreated: () => void }) => {
  const [name, setName] = useState("");
  const [tier, setTier] = useState("community");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    setName("");
    setTier("community");
    setError("");
    onClose();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Please enter an organization name.");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      await makeRequest("/v1/organizations", {
        method: "POST",
        body: { name: name.trim(), tier },
      });
      handleClose();
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create organization.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title="New Organization"
      size="sm"
      onSave={handleSave}
      saveLabel="Create"
      isSaving={isSaving}
    >
      <View className="p-4 flex flex-col gap-4">
        <InputField
          label="Name"
          placeholder="Acme Inc."
          value={name}
          onChange={setName}
        />
        <DropdownField
          label="Tier"
          options={TIER_OPTIONS}
          value={tier}
          onChange={setTier}
        />
        {error && <Text className="text-sm text-red-500">{error}</Text>}
      </View>
    </Dialog>
  );
};

const AddFundsDialog = ({
  isOpen,
  organizationId,
  organizationName,
  onClose,
}: {
  isOpen: boolean;
  organizationId: string | null;
  organizationName: string;
  onClose: () => void;
}) => {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    setAmount("");
    setDescription("");
    setError("");
    onClose();
  };

  const handleSave = async () => {
    const dollars = parseFloat(amount);
    if (isNaN(dollars) || dollars <= 0) {
      setError("Please enter a valid dollar amount.");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      await makeRequest("/v1/credits/adjust", {
        method: "POST",
        body: {
          organizationId,
          type: "credit",
          amount: Math.round(dollars * 1_000_000),
          description: description || undefined,
        },
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add funds.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={`Add Funds — ${organizationName}`}
      size="sm"
      onSave={handleSave}
      saveLabel="Add Funds"
      isSaving={isSaving}
    >
      <View className="p-4 flex flex-col gap-4">
        <InputField
          label="Amount ($)"
          placeholder="10.00"
          type="number"
          value={amount}
          onChange={setAmount}
        />
        <InputField
          label="Description (optional)"
          placeholder="e.g. Courtesy credit"
          value={description}
          onChange={setDescription}
        />
        {error && <Text className="text-sm text-red-500">{error}</Text>}
      </View>
    </Dialog>
  );
};

export const AdminOrganizationsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [range, setRange] = useState<RangeKey>("30d");
  const [searchText, setSearchText] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isAddFundsOpen, setIsAddFundsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [isEditLoaded, setIsEditLoaded] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editError, setEditError] = useState("");
  const [editForm, setEditForm] = useState<EditFormState>(EMPTY_EDIT_FORM);
  const editFetchSeq = useRef(0);

  const selectedId = searchParams.get("id");

  const query = useMemo(() => {
    const { startDate, endDate } = rangeToDates(range);
    const params = new URLSearchParams();
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    return params.toString();
  }, [range]);

  const {
    data: tableResponse,
    isLoading: isLoadingTable,
    error: tableError,
    refresh: refreshTable,
  } = useAdminFetch<{ data: OrgActivityRow[] }>(`/v1/admin/activity/organizations?${query}`);

  const {
    data: detailResponse,
    isLoading: isLoadingDetail,
    error: detailError,
    refresh: refreshDetail,
  } = useAdminFetch<{ data: OrgActivityDetail }>(selectedId ? `/v1/admin/activity/organizations/${selectedId}?${query}` : null);

  const rows = useMemo(() => tableResponse?.data ?? [], [tableResponse]);
  const detail = detailResponse?.data ?? null;

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => row.name?.toLowerCase().includes(q) || row.organizationId.toLowerCase().includes(q));
  }, [rows, searchText]);

  const selectedRow = useMemo(() => rows.find((row) => row.organizationId === selectedId) ?? null, [rows, selectedId]);
  const selectedName = detail?.organization.name ?? selectedRow?.name ?? "Organization";
  const selectedTier = detail?.organization.tier ?? selectedRow?.tier;
  const selectedCreatedAt = detail?.organization.createdAt ?? selectedRow?.createdAt;

  useEffect(() => {
    editFetchSeq.current++;
    setIsEditOpen(false);
    setIsEditLoaded(false);
    setEditError("");
    setDeleteError("");
    setEditForm(EMPTY_EDIT_FORM);
  }, [selectedId]);

  const openDrawer = (organizationId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("id", organizationId);
    setSearchParams(next);
  };

  const closeDrawer = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("id");
    setSearchParams(next);
  };

  const handleToggleEdit = async () => {
    if (isEditOpen) {
      setIsEditOpen(false);
      return;
    }
    if (!selectedId) return;

    setIsEditOpen(true);
    setIsLoadingEdit(true);
    setIsEditLoaded(false);
    setEditError("");

    // Drop stale responses: a slow fetch for a previously selected org must not
    // seed (or clear loading state for) the currently selected org's form.
    const seq = ++editFetchSeq.current;
    try {
      const { data } = await makeRequest<{ data: OrganizationSettings }>(`/v1/organizations/${selectedId}`);
      if (seq !== editFetchSeq.current) return;
      setEditForm({
        tier: data.tier,
        outboundEnabled: data.outboundEnabled,
        sessionCostPerMinute: data.sessionCostPerMinute != null ? String(data.sessionCostPerMinute) : "",
        evalsTokenCostMultiplier: data.evalsTokenCostMultiplier != null ? String(data.evalsTokenCostMultiplier) : "",
        completionsTokenCostMultiplier: data.completionsTokenCostMultiplier != null ? String(data.completionsTokenCostMultiplier) : "",
        phoneNumberMonthlyRate: data.phoneNumberMonthlyRate != null ? String(data.phoneNumberMonthlyRate) : "",
      });
      setIsEditLoaded(true);
    } catch (err) {
      if (seq !== editFetchSeq.current) return;
      setEditError(err instanceof Error ? err.message : "Failed to load organization settings.");
    } finally {
      if (seq === editFetchSeq.current) setIsLoadingEdit(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedId) return;

    setIsSavingEdit(true);
    setEditError("");

    try {
      const body: Record<string, unknown> = {
        tier: editForm.tier,
        outboundEnabled: editForm.outboundEnabled,
      };
      const numericKeys = ["sessionCostPerMinute", "evalsTokenCostMultiplier", "completionsTokenCostMultiplier", "phoneNumberMonthlyRate"] as const;
      for (const key of numericKeys) {
        const parsed = parseFloat(editForm[key]);
        if (!isNaN(parsed)) body[key] = parsed;
      }

      await makeRequest(`/v1/organizations/${selectedId}`, { method: "PATCH", body });
      setIsEditOpen(false);
      refreshTable();
      refreshDetail();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Failed to save changes.");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;

    setIsDeleting(true);
    setDeleteError("");

    try {
      await makeRequest(`/v1/organizations/${selectedId}`, { method: "DELETE" });
      setIsDeleteOpen(false);
      closeDrawer();
      refreshTable();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Failed to delete organization.");
    } finally {
      setIsDeleting(false);
    }
  };

  const numberColumn = (key: string, header: string, value: (row: OrgActivityRow) => number, width = "80px"): AdminColumn<OrgActivityRow> => ({
    key,
    header,
    width,
    align: "right",
    render: (row) => formatCount(value(row)),
    sortValue: value,
  });

  const dollarColumn = (key: string, header: string, value: (row: OrgActivityRow) => number, width = "96px"): AdminColumn<OrgActivityRow> => ({
    key,
    header,
    width,
    align: "right",
    render: (row) => <span className={value(row) === 0 ? "text-gray-400 dark:text-gray-500" : ""}>{formatDollars(value(row))}</span>,
    sortValue: value,
  });

  const columns: AdminColumn<OrgActivityRow>[] = [
    {
      key: "organization",
      header: "Organization",
      width: "1.6fr",
      render: (row) => (
        <View className="flex min-w-0 flex-row items-center gap-2.5">
          <OrgAvatar name={row.name} />
          <Text className="truncate text-sm font-medium text-gray-900 dark:text-white">{row.name}</Text>
          {row.tier === "enterprise" && <Badge variant="info">Enterprise</Badge>}
        </View>
      ),
      sortValue: (row) => row.name?.toLowerCase() ?? null,
    },
    numberColumn("members", "Members", (row) => row.members, "90px"),
    {
      key: "agents",
      header: "Agents",
      width: "90px",
      align: "right",
      render: (row) => (
        <span>
          {formatCount(row.agentsTotal)}
          {row.agentsCreated > 0 && <span className="text-gray-400 dark:text-gray-500"> +{row.agentsCreated}</span>}
        </span>
      ),
      sortValue: (row) => row.agentsTotal,
    },
    numberColumn("sessions", "Sessions", (row) => row.sessions, "96px"),
    numberColumn("releases", "Releases", (row) => row.releases, "90px"),
    numberColumn("pushes", "Pushes", (row) => row.pushes),
    numberColumn("phoneNumbers", "Numbers", (row) => row.phoneNumbers, "90px"),
    numberColumn("invites", "Invites", (row) => row.invites),
    numberColumn("bugTickets", "Bugs", (row) => row.bugTickets, "70px"),
    dollarColumn("creditsUploaded", "Uploaded", (row) => row.creditsUploaded),
    dollarColumn("creditsSpent", "Spent", (row) => row.creditsSpent),
    {
      key: "lastActivityAt",
      header: "Last active",
      width: "110px",
      align: "right",
      render: (row) => <span className={row.lastActivityAt ? "" : "text-gray-400 dark:text-gray-500"}>{formatRelative(row.lastActivityAt)}</span>,
      sortValue: (row) => (row.lastActivityAt ? new Date(row.lastActivityAt).getTime() : null),
    },
  ];

  const metrics = detail?.metrics;
  const periodLabel = range === "all" ? "All time" : `Last ${range}`;
  const statTiles = metrics
    ? [
        { label: "Sessions", value: formatCount(metrics.sessions) },
        { label: "Releases", value: formatCount(metrics.releases) },
        { label: "Pushes", value: formatCount(metrics.pushes) },
        { label: "Agents", value: `${formatCount(metrics.agentsTotal)}${metrics.agentsCreated > 0 ? ` (+${metrics.agentsCreated})` : ""}` },
        { label: "Phone numbers", value: formatCount(metrics.phoneNumbers) },
        { label: "Bug tickets", value: formatCount(metrics.bugTickets) },
        { label: "Dollars uploaded", value: formatDollars(metrics.creditsUploaded) },
        { label: "Dollars spent", value: formatDollars(metrics.creditsSpent) },
      ]
    : [];

  return (
    <PageLayout
      title="Organizations"
      description="Development activity and engagement by organization."
      headerAction={
        <View className="flex flex-row items-center gap-3">
          <RangePicker
            value={range}
            onChange={setRange}
          />
          <Button
            variant="brand"
            size="sm"
            onClick={() => setIsCreateOpen(true)}
          >
            <Icon
              name="add"
              size={14}
            />
            New organization
          </Button>
        </View>
      }
    >
      <View className="mt-6 flex flex-col gap-3">
        {tableError && (
          <Alert variant="destructive">
            <AlertDescription>{tableError}</AlertDescription>
          </Alert>
        )}
        <View className="max-w-sm">
          <InputField
            placeholder="Search organizations…"
            value={searchText}
            onChange={setSearchText}
          />
        </View>
        <AdminTable
          columns={columns}
          rows={filteredRows}
          rowKey={(row) => row.organizationId}
          onRowClick={(row) => openDrawer(row.organizationId)}
          selectedKey={selectedId}
          isLoading={isLoadingTable}
          defaultSort={{ key: "sessions", dir: "desc" }}
          emptyState={{
            icon: "storefront",
            title: searchText ? "No organizations match your search" : "No organizations yet",
          }}
        />
      </View>

      <AdminDetailDrawer
        isOpen={!!selectedId}
        onClose={closeDrawer}
        width={520}
        title={
          <>
            {selectedName}
            {selectedTier === "enterprise" && (
              <Badge
                variant="info"
                className="ml-2"
              >
                Enterprise
              </Badge>
            )}
          </>
        }
        subtitle={selectedCreatedAt ? `Created ${formatDateShort(selectedCreatedAt)}` : undefined}
        headerActions={
          <>
            <IconButton
              icon="payments"
              variant="info"
              title="Add funds"
              onClick={() => setIsAddFundsOpen(true)}
            />
            <IconButton
              icon="edit"
              variant="info"
              title="Edit organization"
              onClick={handleToggleEdit}
            />
          </>
        }
      >
        {detailError && (
          <Alert variant="destructive">
            <AlertDescription>{detailError}</AlertDescription>
          </Alert>
        )}

        {isLoadingDetail && !detail ? (
          <View className="flex justify-center py-10">
            <Spinner size={24} />
          </View>
        ) : (
          detail && (
            <>
              <Section title={`Activity — ${periodLabel}`}>
                <View className="grid grid-cols-2 gap-2">
                  {statTiles.map((tile) => (
                    <View
                      key={tile.label}
                      className="rounded-lg border border-gray-200 dark:border-gray-800 p-3"
                    >
                      <Text className="block text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 truncate">{tile.label}</Text>
                      <Text className="block text-lg font-semibold tabular-nums text-gray-900 dark:text-white">{tile.value}</Text>
                    </View>
                  ))}
                </View>
              </Section>

              <Section title="Sessions">
                <ActivityBarChart
                  data={detail.sessionsSeries}
                  color={ADMIN_CHART_COLORS.sessions}
                  height={140}
                  valueLabel="sessions"
                />
              </Section>

              <Section title="Members">
                {detail.members.length === 0 ? (
                  <Text className="text-sm text-gray-400 dark:text-gray-500">No members</Text>
                ) : (
                  <View className="flex flex-col">
                    {detail.members.map((member) => {
                      const parts = [
                        member.agentsCreated > 0 && `${member.agentsCreated} agents`,
                        member.releases > 0 && `${member.releases} releases`,
                        member.bugTickets > 0 && `${member.bugTickets} bugs`,
                        member.deploys > 0 && `${member.deploys} deploys`,
                      ].filter(Boolean);
                      return (
                        <View
                          key={member.userId}
                          className="flex flex-row items-center gap-2.5 py-1.5"
                        >
                          <OrgAvatar name={member.name || member.email} />
                          <View className="flex min-w-0 flex-1 flex-col">
                            <Text className="truncate text-sm font-medium text-gray-900 dark:text-white">{member.name || member.email}</Text>
                            {member.name && <Text className="truncate text-xs text-gray-400 dark:text-gray-500">{member.email}</Text>}
                          </View>
                          <Badge variant="default">{member.role}</Badge>
                          {parts.length > 0 && <Text className="shrink-0 text-xs tabular-nums text-gray-400 dark:text-gray-500">{parts.join(" · ")}</Text>}
                        </View>
                      );
                    })}
                  </View>
                )}
              </Section>

              {(detail.serviceAccounts?.length ?? 0) > 0 && (
                <Section title="Service Accounts">
                  <View className="flex flex-col">
                    {detail.serviceAccounts.map((account) => {
                      const parts = [
                        account.agentsCreated > 0 && `${account.agentsCreated} agents`,
                        account.releases > 0 && `${account.releases} releases`,
                      ].filter(Boolean);
                      return (
                        <View
                          key={account.serviceAccountId}
                          className="flex flex-row items-center gap-2.5 py-1.5"
                        >
                          <OrgAvatar name={account.name} />
                          <View className="flex min-w-0 flex-1 flex-col">
                            <Text className="truncate text-sm font-medium text-gray-900 dark:text-white">{account.name}</Text>
                          </View>
                          <Badge variant="default">service account</Badge>
                          {parts.length > 0 && <Text className="shrink-0 text-xs tabular-nums text-gray-400 dark:text-gray-500">{parts.join(" · ")}</Text>}
                        </View>
                      );
                    })}
                  </View>
                </Section>
              )}

              {detail.invites.length > 0 && (
                <Section title="Invites">
                  <View className="flex flex-col">
                    {detail.invites.map((invite) => (
                      <View
                        key={`${invite.email}-${invite.createdAt}`}
                        className="flex flex-row items-center gap-2.5 py-1.5"
                      >
                        <View className="flex min-w-0 flex-1 flex-col">
                          <Text className="truncate text-sm text-gray-900 dark:text-gray-100">{invite.email}</Text>
                          <Text className="text-xs text-gray-400 dark:text-gray-500">
                            {invite.role} · {formatRelative(invite.createdAt)}
                          </Text>
                        </View>
                        <Badge variant={inviteStatusVariant(invite.status)}>{invite.status}</Badge>
                      </View>
                    ))}
                  </View>
                </Section>
              )}

              <Section title="Agents">
                {detail.agents.length === 0 ? (
                  <Text className="text-sm text-gray-400 dark:text-gray-500">No agents</Text>
                ) : (
                  <View className="flex flex-col">
                    {detail.agents.map((agent) => (
                      <View
                        key={agent.id}
                        className="flex flex-row items-center gap-2.5 py-1.5"
                      >
                        <View className="flex min-w-0 flex-1 flex-col">
                          <Text className="truncate text-sm font-medium text-gray-900 dark:text-white">{agent.name || agent.id}</Text>
                          <Text className="text-xs text-gray-400 dark:text-gray-500">
                            Created {formatDateShort(agent.createdAt)} · last active {formatRelative(agent.lastActivityAt)}
                          </Text>
                        </View>
                        <Text className="shrink-0 text-xs tabular-nums text-gray-400 dark:text-gray-500">{formatCount(agent.sessions)} sessions</Text>
                      </View>
                    ))}
                  </View>
                )}
              </Section>
            </>
          )
        )}

        {isEditOpen && (
          <Section title="Edit organization">
            {isLoadingEdit ? (
              <View className="flex justify-center py-6">
                <Spinner size={20} />
              </View>
            ) : (
              <View className="flex flex-col gap-4">
                <DropdownField
                  label="Tier"
                  options={TIER_OPTIONS}
                  value={editForm.tier}
                  onChange={(value) => setEditForm((form) => ({ ...form, tier: value }))}
                />
                <SwitchField
                  label="Outbound Enabled"
                  value={editForm.outboundEnabled ? "true" : "false"}
                  onChange={(value) => setEditForm((form) => ({ ...form, outboundEnabled: value === "true" }))}
                  options={[
                    { label: "Disabled", value: "false" },
                    { label: "Enabled", value: "true" },
                  ]}
                />
                <InputField
                  label="Session Cost ($/min)"
                  type="number"
                  value={editForm.sessionCostPerMinute}
                  onChange={(value) => setEditForm((form) => ({ ...form, sessionCostPerMinute: value }))}
                />
                <InputField
                  label="Eval Token Multiplier"
                  type="number"
                  value={editForm.evalsTokenCostMultiplier}
                  onChange={(value) => setEditForm((form) => ({ ...form, evalsTokenCostMultiplier: value }))}
                />
                <InputField
                  label="Completions Token Multiplier"
                  type="number"
                  value={editForm.completionsTokenCostMultiplier}
                  onChange={(value) => setEditForm((form) => ({ ...form, completionsTokenCostMultiplier: value }))}
                />
                <InputField
                  label="Phone # Rate ($/mo)"
                  type="number"
                  value={editForm.phoneNumberMonthlyRate}
                  onChange={(value) => setEditForm((form) => ({ ...form, phoneNumberMonthlyRate: value }))}
                />
                {editError && <Text className="text-sm text-red-500">{editError}</Text>}
                <View className="flex flex-row justify-end">
                  <Button
                    variant="brand"
                    size="sm"
                    onClick={handleSaveEdit}
                    isDisabled={isSavingEdit || !isEditLoaded}
                  >
                    {isSavingEdit && (
                      <Icon
                        name="loader"
                        size={14}
                        className="animate-spin"
                      />
                    )}
                    Save changes
                  </Button>
                </View>
              </View>
            )}
          </Section>
        )}

        <Section title="Danger zone">
          <View className="flex flex-row">
            <Button
              variant="danger"
              size="sm"
              outline
              onClick={() => setIsDeleteOpen(true)}
            >
              <Icon
                name="delete"
                size={14}
              />
              Delete organization
            </Button>
          </View>
        </Section>

        <Field
          label="ID"
          mono
        >
          {selectedId}
        </Field>
      </AdminDetailDrawer>

      <CreateOrganizationDialog
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={refreshTable}
      />

      <AddFundsDialog
        isOpen={isAddFundsOpen}
        organizationId={selectedId}
        organizationName={selectedName}
        onClose={() => setIsAddFundsOpen(false)}
      />

      <Dialog
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteError("");
        }}
        title="Delete Organization"
        size="sm"
        actions={
          <>
            <Button
              variant="neutral"
              size="sm"
              outline
              onClick={() => {
                setIsDeleteOpen(false);
                setDeleteError("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              outline
              onClick={handleDelete}
              isDisabled={isDeleting}
            >
              {isDeleting ? (
                <Icon
                  name="loader"
                  size={14}
                  className="animate-spin"
                />
              ) : (
                <Icon
                  name="trash"
                  size={14}
                />
              )}
              Delete
            </Button>
          </>
        }
      >
        <View className="p-4 flex flex-col gap-3">
          <Text className="text-sm text-gray-900 dark:text-gray-100">Delete {selectedName}? This cannot be undone.</Text>
          {deleteError && <Text className="text-sm text-red-500">{deleteError}</Text>}
        </View>
      </Dialog>
    </PageLayout>
  );
};
